import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import WebViewer from '@pdftron/webviewer';
import { setupWebViewerPreviewInstance } from '../../utils/webViewerFitWidth';
import { OverlayDrawer } from '@fluentui/react-components';
import { ArrowDownload16Regular, Dismiss20Regular } from '@fluentui/react-icons';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { GetAuthenticatedDocUrl } from '../../redux/documentsSlice';
import { attachmentToViewerFile, EXCEL_TOO_LARGE_PREVIEW_MESSAGE, getFileExtensionFromFile, getWebViewerIframeOptions, isPDFTronSupportedAttachment } from '../../utils/filePreviewUtils';
import { downloadTaskAttachmentFile } from '../../utils/taskAttachmentDownloadUtils';
import {
    classifyDocumentLoadError,
    fetchDocumentProtectionCheck,
    getProtectedDocumentMessage,
} from '../../utils/protectedDocumentUtils';
import { getExtensionIcon, getFileSize } from '../../utils/commonUtils';
import { toast } from 'react-toastify';
import { useSuppressThirdPartyWidgets } from '../../hooks/useSuppressThirdPartyWidgets';
import styles from './css/index.module.css';

const TaskAttachmentPreviewPanel = ({ attachment, onClose }) => {
    const dispatch = useDispatch();
    const viewerRef = useRef(null);
    const instanceRef = useRef(null);
    const [fileUrl, setFileUrl] = useState(null);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [error, setError] = useState(null);

    const isOpen = Boolean(attachment);

    useSuppressThirdPartyWidgets(isOpen);

    const canPreview = isPDFTronSupportedAttachment(attachment);
    const extension = useMemo(
        () => getFileExtensionFromFile(attachmentToViewerFile(attachment)),
        [attachment?.id, attachment?.file, attachment?.original_filename, attachment?.extension],
    );

    const clearViewerContainer = useCallback(() => {
        if (viewerRef.current) {
            viewerRef.current.innerHTML = '';
        }
        instanceRef.current = null;
    }, []);

    const handleDownload = useCallback(async () => {
        await downloadTaskAttachmentFile(dispatch, attachment);
    }, [attachment, dispatch]);

    useEffect(() => {
        if (!attachment?.file) {
            setFileUrl(null);
            setError(null);
            return undefined;
        }

        if (!canPreview) {
            setFileUrl(null);
            setPdfLoading(false);
            return undefined;
        }

        let cancelled = false;
        setPdfLoading(true);
        setError(null);

        (async () => {
            const documentUrl = attachment.file;
            try {
                if (documentUrl.includes('.blob.core.windows.net')) {
                    const authenticatedUrl = await dispatch(GetAuthenticatedDocUrl(documentUrl)).unwrap();
                    if (!cancelled) setFileUrl(authenticatedUrl);
                } else if (!cancelled) {
                    setFileUrl(documentUrl);
                }
            } catch (e) {
                if (!cancelled) {
                    setError('Could not load a secure link for this file.');
                    setPdfLoading(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [attachment, canPreview, dispatch]);

    useEffect(() => {
        if (!fileUrl || !canPreview || !viewerRef.current) return undefined;

        setPdfLoading(true);
        setError(null);
        const fileExtension = extension;
        let disposed = false;

        const blockProtectedPreview = reason => {
            const message = getProtectedDocumentMessage(reason);
            toast.warning(message, {
                position: 'top-right',
                toastId: `protected-attachment-${attachment?.id}`,
            });
            setError(message);
            setPdfLoading(false);
        };

        const initWebViewer = async () => {
            const protection = await fetchDocumentProtectionCheck(fileUrl, fileExtension);
            if (disposed) return;

            if (protection.protected) {
                blockProtectedPreview(protection.reason);
                return;
            }

            WebViewer.Iframe(
                getWebViewerIframeOptions({
                    fileUrl,
                    extension: fileExtension,
                    filename: attachment?.original_filename,
                }),
                viewerRef.current,
            )
                .then(instance => {
                    if (disposed) return;
                    instanceRef.current = instance;

                    setupWebViewerPreviewInstance(instance, {
                        extension: fileExtension,
                        fileUrl,
                        filename: attachment?.original_filename,
                        isDisposed: () => disposed,
                        onReady: () => setPdfLoading(false),
                        onDocumentLoadError: err => {
                            const kind = classifyDocumentLoadError(err);
                            if (kind === 'protected') {
                                blockProtectedPreview('generic_protected');
                                return;
                            }
                            if (kind === 'excel_too_large') {
                                instance.UI.closeElements(['errorModal']);
                                setError(EXCEL_TOO_LARGE_PREVIEW_MESSAGE);
                                return;
                            }
                            setError('Failed to open this document.');
                        },
                    });
                })
                .catch(err => {
                    console.error('Task attachment viewer init error:', err);
                    if (!disposed) {
                        setPdfLoading(false);
                        setError('Failed to initialize the viewer.');
                    }
                });
        };

        initWebViewer();

        return () => {
            disposed = true;
            clearViewerContainer();
        };
    }, [fileUrl, canPreview, extension, attachment?.id, attachment?.original_filename, clearViewerContainer]);

    return (
        <OverlayDrawer
            size="full"
            position="bottom"
            open={isOpen}
            onOpenChange={(_, state) => {
                if (!state.open) onClose?.();
            }}>
            {attachment && (
                <div className={styles.taskAttachmentPreviewModal}>
                    <div className={styles.taskAttachmentPreviewHeader}>
                        <div className={styles.taskAttachmentPreviewTitleWrap}>
                            {getExtensionIcon(extension, 20, 20)}
                            <span className={styles.taskAttachmentPreviewTitle} title={attachment.original_filename}>
                                {attachment.original_filename}
                            </span>
                            {attachment.file_size ? (
                                <span className={styles.taskAttachmentPreviewMeta}>{getFileSize(attachment.file_size)}</span>
                            ) : null}
                        </div>
                        <div className={styles.taskAttachmentPreviewActions}>
                            <button type="button" className={styles.taskAttachmentPreviewActionBtn} onClick={handleDownload} title="Download">
                                <ArrowDownload16Regular />
                            </button>
                            <button type="button" className={styles.taskAttachmentPreviewActionBtn} onClick={onClose} title="Close preview">
                                <Dismiss20Regular />
                            </button>
                        </div>
                    </div>
                    <div className={styles.taskAttachmentPreviewBody}>
                        {canPreview ? (
                            <>
                                {error && <p className={styles.taskAttachmentPreviewError}>{error}</p>}
                                <div key={attachment.id} className={styles.taskAttachmentPreviewViewport}>
                                    <div ref={viewerRef} className={styles.taskAttachmentPreviewViewerMount} />
                                    {pdfLoading && (
                                        <div className={styles.taskAttachmentPreviewLoading}>
                                            <AiOutlineLoading3Quarters className="rotateLoader" size={32} />
                                        </div>
                                    )}
                                </div>
                            </>
                        ) : (
                            <div className={styles.taskAttachmentPreviewFallback}>
                                {getExtensionIcon(extension, 40, 40)}
                                <p>Preview is not available for this file type.</p>
                                <button type="button" className="btn btn-primary btn-sm" onClick={handleDownload}>
                                    Download file
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </OverlayDrawer>
    );
};

export default TaskAttachmentPreviewPanel;
